import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import classNames from "classnames";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import CloudUpload from "@material-ui/icons/CloudUpload";
import EditLocationIcon from "@material-ui/icons/EditLocation";
import FindInPageIcon from "@material-ui/icons/FindInPage";
import PlaylistAddCheckIcon from "@material-ui/icons/PlaylistAddCheck";
import * as api from "../../api/recipientLists";
import { PrimaryButtonContained, Tooltip } from "../../components";

import { AddCircle } from "../../icons";
import Paper from "../Paper";
import { NavLink } from "react-router-dom";
import { useDispatch } from "react-redux";
import { createNewRecipientList } from "../../actions/newRecipientList";
import useAuthorized, {
  RECIPIENTLIST_MASTERDATA_PERMISSION,
} from "../../hooks/useAuthorized";

const StyledMenu = withStyles({
  root: {},
})((props) => (
  <Menu
    elevation={0}
    getContentAnchorEl={null}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "center",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "center",
    }}
    {...props}
  />
));

const useStyles = makeStyles((theme) => ({
  link: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    padding: theme.spacing(0.75, 2, 0.75, 2),
    textDecoration: "none",
    "&:hover:not(.disabled)": {
      "background-color": "revert",
    },
    "&.disabled": {
      color: theme.palette.text.disabled,
      cursor: "not-allowed",
    },
  },
}));

const StyledMenuItem = withStyles((theme) => ({
  root: {
    padding: 0,
  },
}))((props) => <MenuItem disableGutters {...props} />);

export default function CustomizedMenus({ newListConfig, isCampaign }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const divisionId = useSelector((state) => state.storage.activeDivisionId);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [enableFilterFromMaster, setEnableFilterFromMaster] = React.useState(
    false
  );
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getRecipientCount = async () => {
    const result = await api.getMasterRecipientsCount(divisionId);
    if (result.totalRecipientCount) {
      setEnableFilterFromMaster(true);
    } else {
      setEnableFilterFromMaster(false);
    }
  };

  useEffect(() => {
    if (newListConfig?.masterList || newListConfig?.masterListUploadFilter) {
      getRecipientCount();
      const interval = setInterval(() => getRecipientCount(), 30000);
      return () => {
        clearInterval(interval);
      };
    }
  }, [newListConfig?.masterList, newListConfig?.masterListUploadFilter]);

  const desktopIcon = <AddCircle />;
  const classes = useStyles();
  const canUseMasterDataUpload = useAuthorized(
    RECIPIENTLIST_MASTERDATA_PERMISSION
  );

  const onCreateCampaign = () => {
    dispatch(createNewRecipientList({ redirect: isCampaign }));
  };
  const handleNavClick = (e) => {
    if (!enableFilterFromMaster) e.preventDefault();
  };
  return (
    <div style={{ marginTop: "8px" }}>
      <PrimaryButtonContained
        startIcon={desktopIcon}
        aria-controls="customized-menu"
        aria-haspopup="true"
        onClick={handleClick}
      >
        New List
      </PrimaryButtonContained>

      <StyledMenu
        id="customized-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {newListConfig?.upload && (
          <StyledMenuItem onClick={onCreateCampaign}>
            <Paper
              elevation={0}
              className={classes.link}
              component={NavLink}
              to={"/recipient-lists/create/file/upload"}
            >
              <ListItemIcon>
                <CloudUpload fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText primary="Upload List" />
            </Paper>
          </StyledMenuItem>
        )}
        {newListConfig?.map && (
          <StyledMenuItem onClick={onCreateCampaign}>
            <Paper
              elevation={0}
              className={classes.link}
              component={NavLink}
              to={"/recipient-lists/create/map/filter"}
            >
              <ListItemIcon>
                <EditLocationIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText primary="Select from Map" />
            </Paper>
          </StyledMenuItem>
        )}
        {newListConfig?.masterList && (
          <Tooltip
            title={
              enableFilterFromMaster ? "" : t("No Recipients in Master List")
            }
          >
            <StyledMenuItem onClick={onCreateCampaign}>
              <Paper
                elevation={0}
                className={classNames(classes.link, {
                  disabled: !enableFilterFromMaster,
                })}
                component={NavLink}
                onClick={handleNavClick}
                to={"/recipient-lists/create/filter/columns"}
              >
                <ListItemIcon>
                  <FindInPageIcon
                    fontSize="small"
                    color={enableFilterFromMaster ? "primary" : "disabled"}
                  />
                </ListItemIcon>
                <ListItemText primary="Filter from Master List" />
              </Paper>
            </StyledMenuItem>
          </Tooltip>
        )}
        {newListConfig?.masterListUploadFilter && canUseMasterDataUpload && (
          <Tooltip
            title={
              enableFilterFromMaster ? "" : t("No Recipients in Master List")
            }
          >
            <StyledMenuItem onClick={onCreateCampaign}>
              <Paper
                elevation={0}
                className={classNames(classes.link, {
                  disabled: !enableFilterFromMaster,
                })}
                component={NavLink}
                onClick={handleNavClick}
                to={"/recipient-lists/create/upload-filter/upload"}
              >
                <ListItemIcon>
                  <PlaylistAddCheckIcon
                    fontSize="small"
                    color={enableFilterFromMaster ? "primary" : "disabled"}
                  />
                </ListItemIcon>
                <ListItemText primary="Upload to Filter from Master List" />
              </Paper>
            </StyledMenuItem>
          </Tooltip>
        )}
      </StyledMenu>
    </div>
  );
}
